import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Radio, Clock, Users, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Live() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/stream-config/public`);
        setConfig(response.data);
      } catch (err) {
        logger.error("Error fetching stream config:", err);
        setConfig({ is_live: false, stream_title: "SMSC Live", stream_description: "" });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();

    // Poll for updates every 30 seconds
    const interval = setInterval(fetchConfig, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-casino-black" data-testid="live-page">
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-casino-black to-casino-dark">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h1 className="font-heading text-3xl md:text-4xl text-white">
                {config?.stream_title || "SMSC Live"}
              </h1>
              {config?.is_live ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full animate-pulse">
                  <Radio className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-bold uppercase tracking-wider">Live</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-white/40" />
                  <span className="text-white/60 text-sm font-medium">Offline</span>
                </div>
              )}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Video Player Area - 3 columns */}
            <div className="lg:col-span-3">
              <div className="relative w-full bg-black rounded-xl overflow-hidden border border-white/10">
                {/* 16:9 Aspect Ratio Container */}
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  {config?.is_live && config?.vdo_embed_url ? (
                    <iframe
                      src={config.vdo_embed_url}
                      className="absolute inset-0 w-full h-full"
                      allow="autoplay; camera; microphone; fullscreen; display-capture; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                      title="SMSC Live Stream"
                      data-testid="live-stream-iframe"
                    />
                  ) : (
                    /* Offline State */
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-casino-dark to-black">
                      {config?.offline_thumbnail ? (
                        <img 
                          src={config.offline_thumbnail} 
                          alt="Stream thumbnail" 
                          className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                      ) : null}
                      <div className="relative z-10 text-center p-8">
                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
                          <Radio className="w-10 h-10 text-gold/50" />
                        </div>
                        <h2 className="font-heading text-2xl md:text-3xl text-white mb-3">
                          We're Currently Offline
                        </h2>
                        <p className="text-white/60 max-w-md mx-auto">
                          Check back soon for live poker action from San Marcos Social Club!
                        </p>
                        <div className="mt-6 flex items-center justify-center gap-2 text-white/40 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>Live streams available during special events</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Live Badge Overlay (when live) */}
                {config?.is_live && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-lg shadow-lg">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-xs font-bold uppercase tracking-wider">Live Now</span>
                  </div>
                )}
              </div>

              {/* Stream Description */}
              {config?.stream_description && (
                <div className="mt-4 p-4 glass-card">
                  <p className="text-white/80">{config.stream_description}</p>
                  {config?.updated_at && (
                    <p className="text-white/40 text-xs mt-2">
                      Last updated: {new Date(config.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - 1 column */}
            <div className="lg:col-span-1 space-y-4">
              {/* Info Panel */}
              <div className="glass-card p-6">
                <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  About the Stream
                </h3>
                <div className="space-y-3 text-sm">
                  <p className="text-white/70">
                    Watch live poker action from San Marcos Social Club, the premier poker room in San Marcos, Texas.
                  </p>
                  <p className="text-white/70">
                    Follow us for tournament coverage, special events, and behind-the-scenes content.
                  </p>
                </div>
              </div>

              {/* Chat Placeholder */}
              <div className="glass-card p-6">
                <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-gold" />
                  Live Chat
                </h3>
                <div className="bg-black/30 rounded-lg p-4 min-h-[200px] flex items-center justify-center">
                  <p className="text-white/40 text-sm text-center">
                    {config?.is_live 
                      ? "Chat coming soon!" 
                      : "Chat available when stream is live"}
                  </p>
                </div>
              </div>

              {/* Quick Links */}
              <div className="glass-card p-6">
                <h3 className="font-heading text-lg text-white mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <a href="/tournaments" className="block text-gold hover:text-gold-light text-sm transition-colors">
                    → Tournament Schedule
                  </a>
                  <a href="/membership" className="block text-gold hover:text-gold-light text-sm transition-colors">
                    → Become a Member
                  </a>
                  <a href="/contact" className="block text-gold hover:text-gold-light text-sm transition-colors">
                    → Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
